import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { FaroToggleView } from "@components/common/toggle/faro-toggle-view";
import { CreditViewMode } from "@custom-types/credit-types";
import { ExportTransactionsOrPurchases } from "@pages/settings/subscription-settings/credits/export-transactions-or-purchases";
import {
  creditTransactionsSelector,
  fetchingTransactionsFlagsSelector,
} from "@store/credit-transactions/credit-transactions-selector";
import { allCreditSubscriptionsSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props {
  /** The current view mode of the toggle */
  currentViewMode: CreditViewMode;

  /** Function to set the view mode */
  setViewMode: (newMode: CreditViewMode) => void;
}

/**
 * List of possible view options for the dialog.
 * It is used for the toggle view component.
 */
const VIEW_OPTIONS = [
  { label: "Transactions", value: CreditViewMode.transaction },
  { label: "Purchases", value: CreditViewMode.purchases },
];

/** Renders additional top right actions for the credits dialog */
export function CreditsDialogTopRightActions({
  currentViewMode,
  setViewMode,
}: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const transactions = useAppSelector(creditTransactionsSelector);
  const { status } = useAppSelector(fetchingTransactionsFlagsSelector);
  const subscriptions = useAppSelector(allCreditSubscriptionsSelector);
  const isTransactionsView = currentViewMode === CreditViewMode.transaction;

  // Show export button if: transactions view has data/loading, or purchases view has data
  const shouldShowExportButton = isTransactionsView
    ? transactions.length > 0 || status === "pending"
    : subscriptions.length > 0;

  /** Handle the view mode change */
  function handleViewModeChange(newMode: CreditViewMode): void {
    trackEvent({
      name:
        newMode === CreditViewMode.transaction
          ? WorkspaceEvents.viewCreditsTransactions
          : WorkspaceEvents.viewCreditsPurchaseHistory,
      props: {
        origin: "credits-dialog",
      },
    });

    setViewMode(newMode);
  }

  return (
    <>
      <FaroToggleView
        options={VIEW_OPTIONS}
        onChange={handleViewModeChange}
        currentViewMode={currentViewMode}
      />
      {shouldShowExportButton && (
        <>
          <SphereActionDivider />
          <ExportTransactionsOrPurchases isTransactionsView={isTransactionsView} />
          <SphereActionDivider />
        </>
      )}
    </>
  );
}
